import { cn } from "../../lib/utils";
import { VariantProps, cva } from "class-variance-authority";
import { ButtonHTMLAttributes } from "react";
import Loading from "../Loading";

import { Status } from "../../lib/types";
import CheckIcon from "../icons/CheckIcon";
import ErrorIcon from "../icons/ErrorIcon";

export const buttonClasses = cva("rounded-md font-medium transition-all transform active:scale-[0.85] disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-80", {
    variants: {
        variant: {
            default: "bg-slate-100 text-slate-900 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-400",
            outline: "border border-slate-200 text-slate-400 hover:border-slate-300 dark:border-slate-800 dark:hover:border-slate-600 ",
            ghost: "hover:opacity-70",
            primary: "bg-blue-600 text-slate-50 hover:bg-blue-500 dark:bg-blue-800 dark:hover:bg-blue-900",
            dark: "bg-slate-800 text-slate-50 hover:bg-slate-700 dark:text-slate-950 dark:bg-slate-200 dark:hover:bg-slate-100",
            suttle: "hover:bg-slate-100 dark:hover:bg-slate-800",
            light: "bg-white text-slate-800 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        },
        textAlign: {
            default: "inline-flex items-center justify-center gap-3",
            left: "inline-flex items-center gap-3"
        },
        size: {
            default: "h-9 py-2 px-6",
            small: "h-8 px-4 rounded-sm text-sm",
            extraSmall: "h-7 px-2 py-1 rounded-sm text-xs",
            large: "h-12 py-4 px-8 text-lg",
            box: "h-9 w-9",
            ghost: "w-fit h-fit"
        }
    },
    defaultVariants: {
        textAlign: "default",
        variant: "default",
        size: "default"
    }
});

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonClasses> {
    className?: string;
    status?: Status;
}

export default function Button({ className, size, variant, textAlign, status = "none", ...props }: ButtonProps) {
    return (
        <button disabled={status !== "none"} className={cn(buttonClasses({ variant, size, textAlign, className }))} {...props}>
            {status !== "none" && (
                <div className="mr-3">
                    {status === "loading" && <Loading size={"small"} color={variant === "dark" ? "dark" : "default"} />}
                    {status === "success" && <CheckIcon color={variant === "dark" ? "dark" : "default"} />}
                    {status === "error" && <ErrorIcon color={variant === "dark" ? "dark" : "default"} />}
                </div>
            )}
            {props.children}
        </button>
    );
}
